import {Stack, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {materializeSnowflakeTable} from 'instruments/connections/ConnectionDAO';
import {SnowflakeTable} from 'instruments/instrument-data/InstrumentDataConstants';
import notifications from 'notifications';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {getUserMessage} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import SnowflakeTableSelector from './SnowflakeTableSelector';

interface SnowflakeExportModalProps {
  connectionId: string;
  connectionName: string;
  onRequestClose: () => void;
}

const GENERIC_FAILURE_MESSAGE = 'Failed to export table. Please try again.';

// Modal that lets the user pick a Snowflake table on the connection and export it into
// a new Benchling Dataset (usable in analyses). Drives the materialize-table endpoint.
export function SnowflakeExportModal(props: SnowflakeExportModalProps): JSX.Element {
  const {connectionId, connectionName, onRequestClose} = props;
  const [error, setError] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<SnowflakeTable | null>(null);
  const [datasetName, setDatasetName] = useState('');

  const effectiveDatasetName = datasetName.trim() || (selectedTable ? selectedTable.table_name : '');
  const canSubmit = Boolean(selectedTable && effectiveDatasetName);

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Export to dataset',
          disabled: !canSubmit,
          submit: true,
        },
      ]}
      title="Export Snowflake table to dataset"
      onRequestClose={onRequestClose}
      closeable={true}
      bannerProps={error != null ? {bannerType: InlineBannerType.ERROR, text: error} : null}
      onRequestSubmit={async () => {
        setError(null);
        if (selectedTable == null) {
          return;
        }
        try {
          await withLoadingScreen(
            materializeSnowflakeTable(
              connectionId,
              selectedTable.schema_name,
              selectedTable.table_name,
              effectiveDatasetName
            ),
            'Exporting table to dataset. This can take a few minutes...'
          );
          notifications.success(
            `Created dataset "${effectiveDatasetName}" from ${selectedTable.table_name}.`
          );
          onRequestClose();
        } catch (err: unknown) {
          const userMessage = getUserMessage(err);
          if (userMessage == null) {
            setError(GENERIC_FAILURE_MESSAGE);
            throw err;
          }
          setError(userMessage);
        }
      }}
    >
      <Stack spacing="md">
        <SnowflakeTableSelector
          connectionId={connectionId}
          value={selectedTable}
          onChange={setSelectedTable}
        />
        <TextInput
          label="Dataset name"
          description="Defaults to the table name if left blank."
          placeholder={selectedTable ? selectedTable.table_name : `${connectionName} data`}
          value={datasetName}
          onChange={(e) => {
            setDatasetName(e.currentTarget.value);
          }}
        />
      </Stack>
    </Modal>
  );
}
